import { FunctionComponent } from 'react';
import { Select, MenuItem } from '@mui/material';
import { QuestDelegationPriority } from 'src/enums/quests';
import { ReactComponent as YellowDotIcon } from 'src/assets/icons/yellow_dot.svg';
import {
    PriorityContainer,
    PriorityLabel,
    StyledFormControl,
    MenuItemContent,
    selectStyles,
    menuProps
} from './styled';

interface Props {
    priority: number;
    onPriorityChange: (priority: number) => void;
    isAdmin: boolean;
    disabled?: boolean;
    showLabel?: boolean;
}

const PriorityDropdown: FunctionComponent<Props> = ({
    priority,
    onPriorityChange,
    isAdmin,
    disabled = false,
    showLabel = true
}: Props) => {
    const renderAdminValue = (value: number | '') => {
        if (!value) return '';
        return value.toString();
    };

    const renderNonAdminValue = (value: number | '') => {
        if (!value) return '';
        if (value >= 1 && value <= 3) return 'Low';
        if (value >= 4 && value <= 6) return 'Medium';
        if (value >= 7 && value <= 10) return 'High';
        return 'Medium';
    };

    const renderValue = isAdmin ? renderAdminValue : renderNonAdminValue;

    return (
        <PriorityContainer>
            {showLabel && <PriorityLabel>Priority</PriorityLabel>}
            <StyledFormControl>
                <Select
                    value={priority ?? ''}
                    onChange={(e) => onPriorityChange(e.target.value as number)}
                    renderValue={renderValue}
                    size="small"
                    MenuProps={menuProps}
                    sx={selectStyles}
                    disabled={disabled}
                >
                    {isAdmin
                        ? // Admin view: numbers 1-10
                          Array.from({ length: 10 }, (_, i) => i + 1).map(
                              (num) => (
                                  <MenuItem key={num} value={num}>
                                      <MenuItemContent>
                                          {num}
                                          {priority === num && (
                                              <YellowDotIcon />
                                          )}
                                      </MenuItemContent>
                                  </MenuItem>
                              )
                          )
                        : // Non-admin view: Low, Medium, High — must be an array, not a Fragment,
                          // because MUI's SelectInput uses React.Children.map which does not flatten Fragments.
                          [
                              <MenuItem
                                  key="low"
                                  value={QuestDelegationPriority.LOW}
                              >
                                  <MenuItemContent>
                                      Low
                                      {priority >= 1 && priority <= 3 && (
                                          <YellowDotIcon />
                                      )}
                                  </MenuItemContent>
                              </MenuItem>,
                              <MenuItem
                                  key="medium"
                                  value={QuestDelegationPriority.MEDIUM}
                              >
                                  <MenuItemContent>
                                      Medium
                                      {priority >= 4 && priority <= 6 && (
                                          <YellowDotIcon />
                                      )}
                                  </MenuItemContent>
                              </MenuItem>,
                              <MenuItem
                                  key="high"
                                  value={QuestDelegationPriority.HIGH}
                              >
                                  <MenuItemContent>
                                      High
                                      {priority >= 7 && priority <= 10 && (
                                          <YellowDotIcon />
                                      )}
                                  </MenuItemContent>
                              </MenuItem>
                          ]}
                </Select>
            </StyledFormControl>
        </PriorityContainer>
    );
};

export default PriorityDropdown;
